import { merge } from 'lodash-es';

import type { Action } from '../actions';
import { emptyTabsOrder } from './tabsOrder/tabOrder.model';
import { tabOrdersReducer } from './tabsOrder/tabOrder.reducer';
import type {
  UserPreferencesDefaultFieldValuesChanged,
  UserPreferencesEssentialExpandedBlocksToggle,
} from './userPreferences.action';
import { type DefaultFieldValues, initialPreferenceState } from './userPreferences.model';
import type { UserPreferencesState } from './userPreferences.types';
export function userPreferencesReducer(
  state: UserPreferencesState = initialPreferenceState,
  action: Action,
): UserPreferencesState {
  switch (action.type) {
    case 'USER_PREFERENCES_RECEIVED':
      return {
        ...action.data,
        precision: merge(state.precision, action.data.precision),
        landingPage: action.data.landingPage ?? 'QuoteWorkspace',
        status: 'ready',
        tabsOrder: action.data.tabsOrder,
        productLayoutPreferences: action.data.productLayoutPreferences,
      };
    case 'USER_PREFERENCES_TIMEOUTED':
    case 'USER_PREFERENCES_FAILED':
      return { ...state, status: 'error' };
    case 'USER_PREFERENCES_DEFAULTED':
      return {
        ...state,
        availableSpProducts: [],
        status: 'ready',
        landingPage: 'QuoteWorkspace',
        isDarkMode: false,
      };
    case 'USER_PREFERENCES_DARK_MODE_CHANGED':
      return { ...state, isDarkMode: action.value };
    case 'USER_PREFERENCES_NOTIFICATION_CONTACTS_CHANGED':
      return { ...state, notifications: action.value };
    case 'USER_PREFERENCES_LANDING_PAGE_CHANGED':
      return { ...state, landingPage: action.value };
    case 'USER_PREFERENCES_DISCOVER_PAGE_REGION_CHANGED':
      return { ...state, discoverPageRegion: action.value };
    case 'USER_PREFERENCES_SECONDARY_MARKET_PRODUCT_SEARCH_CODE_TYPE_CHANGED':
      return { ...state, productCodeType: action.codeType };
    case 'USER_PREFERENCES_SECONDARY_MARKET_IS_CLOSE_TAB_CLOSE_TAB_WHEN_LIVE_TRADE_CP_ORDER_IS_PLACED_CHANGED':
      return { ...state, closeTabWhenLiveTradeCpOrderIsPlacedActivated: action.value };
    case 'USER_PREFERENCES_QUOTE_WORKSPACE_VERSION_CHANGED':
      return { ...state, quoteWorkspaceVersion: action.value };
    case 'USER_PREFERENCES_BATCH_WORKSPACE_VERSION_CHANGED':
      return { ...state, batchWorkspaceVersion: action.value };
    case 'USER_PREFERENCES_UNDERLYING_CODE_TYPE_CHANGED':
      return { ...state, underlyingCodeType: action.value };
    case 'USER_PREFERENCES_LANGUAGE_CHANGED':
      return { ...state, languageIsoCode: action.language };
    case 'USER_PREFERENCES_DEFAULT_FIELD_VALUES_CHANGED':
      return onDefaultValuesChanged(state, action);
    case 'USER_PREFERENCES_DISPLAY_PRECISION_CHANGED':
      return { ...state, precision: { ...state.precision, [action.key]: action.value } };
    case 'USER_PREFERENCES_NEVER_SHOW_BATCH_WORKSPACE_SHORTCUT_CHEATSHEET_BANNER_CHANGED':
      return { ...state, showBatchShortcutCheatsheet: false };
    case 'USER_PREFERENCES_SPMTABS_UPDATED':
      return {
        ...state,
        tabsOrder: tabOrdersReducer(state.tabsOrder ?? emptyTabsOrder, action),
      };
    case 'USER_PREFERENCES_AVAILABLE_CURRENCIES_RECEIVED':
      return { ...state, availableCurrencies: action.currencies };
    case 'USER_PREFERENCES_PINNED_PRODUCTS_VARIATIONS_CHANGED':
      return { ...state, pinnedProductVariations: action.productVariations };
    case 'USER_PREFERENCES_ESSENTIAL_PINNED_PRODUCTS_VARIATIONS_CHANGED':
      return { ...state, essentialPinnedProductVariations: action.productVariations };
    case 'USER_PREFERENCES_ESSENTIAL_EXPANDED_BLOCKS_TOGGLE':
      return essentialExpandedBlocksReducer(state, action);
    case 'USER_PREFERENCES_PRODUCT_LAYOUT_UPDATE_SUCCEED':
      return { ...state, productLayoutPreferences: action.productLayoutPrefs };
    default:
      return state;
  }
}

function essentialExpandedBlocksReducer(
  state: UserPreferencesState,
  action: UserPreferencesEssentialExpandedBlocksToggle,
) {
  const { blockId, productType, productSubType } = action;
  const currentExpandedBlocks = state.essentialExpandedBlocks?.[productType]?.[productSubType] ?? [];
  const isBlockExpanded = currentExpandedBlocks.includes(blockId);

  const updatedExpandedBlocks = isBlockExpanded
    ? currentExpandedBlocks.filter(id => id !== blockId)
    : [...currentExpandedBlocks, blockId];

  return {
    ...state,
    essentialExpandedBlocks: {
      ...state.essentialExpandedBlocks,
      [productType]: {
        ...state.essentialExpandedBlocks?.[productType],
        [productSubType]: updatedExpandedBlocks,
      },
    },
  };
}

function onDefaultValuesChanged(
  state: UserPreferencesState,
  action: UserPreferencesDefaultFieldValuesChanged,
): UserPreferencesState {
  if (action.key === 'productType') {
    return onProductTypeChanged(state, <string>action.value);
  }

  if (action.key === 'productSubtype') {
    return onProductSubtypeChanged(state, <string>action.value);
  }

  return {
    ...state,
    defaultFieldValues: <DefaultFieldValues>{ ...state.defaultFieldValues, [action.key]: action.value },
  };
}

function onProductSubtypeChanged(state: UserPreferencesState, productSubtype: string): UserPreferencesState {
  const newAvailableWrappers = state
    .availableSpProducts!.filter(product => product.productSubType === productSubtype)
    .map(product => product.wrapper);
  const actualWrapper = state.defaultFieldValues!.wrapper;
  const newWrapper = newAvailableWrappers!.includes(actualWrapper) ? actualWrapper : newAvailableWrappers[0];
  return {
    ...state,
    defaultFieldValues: <DefaultFieldValues>{
      ...state.defaultFieldValues,
      productSubtype,
      wrapper: newWrapper,
    },
  };
}

function onProductTypeChanged(state: UserPreferencesState, productType: string): UserPreferencesState {
  const newAvailableProductSubtypes = state
    .availableSpProducts!.filter(product => product.productType === productType)
    .map(product => product.productSubType);

  const newProductSubtype = newAvailableProductSubtypes[0];
  const actualWrapper = state.defaultFieldValues!.wrapper;
  const newAvailableWrappers = state
    .availableSpProducts!.filter(product => product.productSubType === newProductSubtype)
    .map(product => product.wrapper);
  const newWrapper = newAvailableWrappers!.includes(actualWrapper) ? actualWrapper : newAvailableWrappers[0];
  if (productType === 'Accumulator' || productType === 'FxDualCurrency') {
    return {
      ...state,
      defaultFieldValues: <DefaultFieldValues>{
        ...state.defaultFieldValues,
        productType,
        currency: '',
        productSubtype: newProductSubtype,
        wrapper: newWrapper,
      },
    };
  }
  return {
    ...state,
    defaultFieldValues: <DefaultFieldValues>{
      ...state.defaultFieldValues,
      productType,
      productSubtype: newProductSubtype,
      wrapper: newWrapper,
    },
  };
}
